import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/icon/icon.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/button/text-button.js";

import "../components/app-shell";
import { sharedStyles } from "../styles/shared";
import { icon } from "../lib/icons";
import { errorMessage, inputValue } from "../lib/dom";
import { toast } from "../lib/toast";
import { api } from "../lib/api";
import { provisioning } from "../lib/provisioning";

/** An unregistered tag identity seen at the public verify endpoint. */
interface UnknownTag {
  id: string;
  uid: string;
  taps: number;
  firstSeen: string;
  lastSeen: string;
  status: string;
  notes: string;
  suspectedOrg?: string;
  suspectedProduct?: string;
  attributionNote?: string;
}

/** A provisioned tenant, offered as an attribution target. */
interface TenantOption {
  orgId: string;
  name: string;
}

interface CounterfeitReport {
  windowDays: number;
  tags: UnknownTag[];
  distinctTags: number;
  totalTaps: number;
}

// Platform-wide counterfeit evidence. These tags belong to no tenant — they
// carry UIDs this platform never provisioned — so only operators see them.
@customElement("counterfeit-page")
export class CounterfeitPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .page {
        padding: var(--space-4, 16px);
        max-width: 1100px;
        margin: 0 auto;
      }
      @media (min-width: 600px) {
        .page {
          padding: var(--space-7, 28px) var(--space-8, 32px);
        }
      }
      .head h1 {
        margin: 0 0 4px;
      }
      .head p {
        margin: 0 0 20px;
        color: var(--md-sys-color-on-surface-variant);
        max-width: 78ch;
      }
      .figures {
        display: flex;
        gap: 28px;
        flex-wrap: wrap;
        margin-bottom: 16px;
      }
      .figures div b {
        display: block;
        font-size: 1.75rem;
        font-weight: 600;
        line-height: 1.2;
      }
      .figures div span {
        font-size: 0.75rem;
        color: var(--md-sys-color-on-surface-variant);
      }
      .uid {
        font-family: ui-monospace, monospace;
        font-size: 0.8125rem;
      }
      .attrib {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 260px;
      }
      td small {
        display: block;
        font-style: italic;
        opacity: 0.75;
      }
      .explain {
        border-radius: 10px;
        padding: 12px 14px;
        background: var(--md-sys-color-surface-variant);
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.8125rem;
        line-height: 1.5;
        max-width: 78ch;
        margin-bottom: 20px;
      }
    `,
  ];

  @state() private report: CounterfeitReport | null = null;
  @state() private tenants: TenantOption[] = [];
  @state() private loading = true;
  @state() private error = "";
  @state() private editing = "";
  @state() private draftOrg = "";
  @state() private draftNote = "";
  @state() private saving = false;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const [report, prospects] = await Promise.all([
        api.get<CounterfeitReport>("/nfc/counterfeit-tags?days=90"),
        // Provisioned prospects are the operator's view of live tenants; a
        // fake can only be attributed to one of those.
        provisioning.prospects().catch(() => []),
      ]);
      this.report = report;
      const seen = new Set<string>();
      this.tenants = [];
      for (const p of prospects) {
        if (!p.tenantOrgId || seen.has(p.tenantOrgId)) continue;
        seen.add(p.tenantOrgId);
        this.tenants.push({ orgId: p.tenantOrgId, name: p.legalName });
      }
    } catch (err) {
      this.error = errorMessage(err, "Failed to load counterfeit evidence.");
    } finally {
      this.loading = false;
    }
  }

  private startEdit(t: UnknownTag): void {
    this.editing = t.id;
    this.draftOrg = t.suspectedOrg ?? "";
    this.draftNote = t.attributionNote ?? "";
  }

  private tenantName(orgId?: string): string {
    if (!orgId) return "";
    return this.tenants.find((t) => t.orgId === orgId)?.name ?? orgId;
  }

  // Attribution is what makes a fake visible to a producer, so it is recorded
  // with the note explaining how it was established.
  private async saveAttribution(t: UnknownTag): Promise<void> {
    this.saving = true;
    try {
      const updated = await api.patch<UnknownTag>(
        `/nfc/counterfeit-tags/${t.id}`,
        { suspectedOrg: this.draftOrg, note: this.draftNote },
      );
      if (this.report) {
        this.report = {
          ...this.report,
          tags: this.report.tags.map((x) => (x.id === t.id ? updated : x)),
        };
      }
      this.editing = "";
      toast(
        this.draftOrg
          ? `Attributed to ${this.tenantName(this.draftOrg)} — now visible on their dashboard`
          : "Attribution cleared",
      );
    } catch (err) {
      toast(errorMessage(err, "Could not save attribution."), true);
    } finally {
      this.saving = false;
    }
  }

  private fmt(iso: string): string {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  }

  private renderBody() {
    const r = this.report;
    if (!r) return null;
    if (r.tags.length === 0) {
      return html`<div class="card empty">
        No unregistered tags have been seen in the last ${r.windowDays} days.
      </div>`;
    }
    return html`
      <div class="figures">
        <div>
          <b>${r.distinctTags.toLocaleString("en")}</b>
          <span>Fake tag identities</span>
        </div>
        <div>
          <b>${r.totalTaps.toLocaleString("en")}</b>
          <span>Taps on them</span>
        </div>
      </div>
      <div class="card card--flush">
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Tag UID</th>
                <th>Taps</th>
                <th>Last seen</th>
                <th>Impersonating</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${r.tags.map(
                (t) => html`
                  <tr>
                    <td><span class="uid">${t.uid}</span></td>
                    <td>
                      ${t.taps > 1
                        ? html`<span class="badge badge--bad"
                            >${icon("flag")} ${t.taps}</span
                          >`
                        : t.taps}
                    </td>
                    <td>${this.fmt(t.lastSeen)}</td>
                    <td>
                      ${this.editing === t.id
                        ? html`<div class="attrib">
                            <md-outlined-select
                              label="Producer"
                              .value=${this.draftOrg}
                              @change=${(e: Event) =>
                                (this.draftOrg = inputValue(e))}
                            >
                              <md-select-option value="">
                                <div slot="headline">— not established —</div>
                              </md-select-option>
                              ${this.tenants.map(
                                (o) => html`<md-select-option value=${o.orgId}>
                                  <div slot="headline">${o.name}</div>
                                </md-select-option>`,
                              )}
                            </md-outlined-select>
                            <md-outlined-text-field
                              label="How was this established?"
                              .value=${this.draftNote}
                              @input=${(e: Event) =>
                                (this.draftNote = inputValue(e))}
                            ></md-outlined-text-field>
                          </div>`
                        : t.suspectedOrg
                          ? html`<b>${this.tenantName(t.suspectedOrg)}</b>
                              ${t.attributionNote
                                ? html`<small>${t.attributionNote}</small>`
                                : null}`
                          : html`<span class="badge badge--neutral"
                              >Not established</span
                            >`}
                    </td>
                    <td>
                      ${this.editing === t.id
                        ? html`<md-text-button
                              ?disabled=${this.saving}
                              @click=${() => void this.saveAttribution(t)}
                              >Save</md-text-button
                            >
                            <md-text-button @click=${() => (this.editing = "")}
                              >Cancel</md-text-button
                            >`
                        : html`<md-text-button
                            @click=${() => this.startEdit(t)}
                            >${t.suspectedOrg ? "Change" : "Attribute"}</md-text-button
                          >`}
                    </td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <app-shell active="/counterfeit">
        <div class="page">
          <div class="head">
            <h1>Counterfeit tags</h1>
            <p>
              Tags that pointed a consumer at this platform carrying a UID we
              never issued.
            </p>
          </div>
          <div class="explain">
            Every genuine tag is provisioned here, so a verification attempt for
            an unknown UID cannot come from a legitimate product — someone
            manufactured a tag to impersonate one. A UID tapped repeatedly means
            copies are circulating rather than a single specimen being tested.
            Nothing about an unregistered UID says whose product it was
            imitating, so a fake stays here until you establish the target —
            from a buyer's report naming the brand, a seized sample, or a
            rights-holder complaint. Attributing one publishes it to that
            producer's dashboard.
          </div>
          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.renderBody()}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "counterfeit-page": CounterfeitPage;
  }
}
